import { memo, useEffect, useState } from "react";
import { Box, HStack, Icon, RippleButton, Text, VStack } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import type { ScoreTest } from "../../types";
import { serverNow } from "../../../../shared/serverClock";
import { formatDateTime } from "../../../../shared/utils";
import { StatusTag } from "./TestCard";
import styles from "./SCOREResumeCard.module.scss";

interface SCOREResumeCardProps {
  test: ScoreTest;
  onResume: (test: ScoreTest) => void;
  className?: string;
}

const splitRemaining = (diffMs: number) => {
  const safe = Math.max(0, Math.floor(diffMs / 1000));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return { h, m, s };
};

const useTickingNow = (enabled: boolean) => {
  const [now, setNow] = useState(() => {
    try {
      return serverNow();
    } catch {
      return Date.now();
    }
  });

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => {
      try {
        setNow(serverNow());
      } catch {
        setNow(Date.now());
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [enabled]);

  return now;
};

interface TimeRemainingProps {
  endIso: string;
}

const TimeRemaining = memo(({ endIso }: TimeRemainingProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");
  const endMs = new Date(endIso).getTime();
  const now = useTickingNow(true);
  const { h, m, s } = splitRemaining(endMs - now);

  return (
    <HStack className={styles.metaItem}>
      <Icon name="clockIcon" size={16} color={theme.colors.error[60]} />
      <Text
        variant="body"
        size="medium"
        weight="emphasized"
        color={theme.colors.error[60]}
      >
        {t("ends_in_format", { h, m, s })}
      </Text>
    </HStack>
  );
});
TimeRemaining.displayName = "TimeRemaining";

interface ResumeButtonProps {
  endIso: string | null;
  isExpiredFlag: boolean;
  onPress: () => void;
}

const ResumeButton = ({
  endIso,
  isExpiredFlag,
  onPress,
}: ResumeButtonProps) => {
  const { t } = useTranslation("tests");
  const now = useTickingNow(!!endIso && !isExpiredFlag);
  const isExpired =
    isExpiredFlag || (endIso ? new Date(endIso).getTime() <= now : false);

  return (
    <RippleButton
      onPress={onPress}
      variant="primary"
      shape="round"
      disabled={isExpired}
      height={44}
      fontSize={16}
    >
      {t("resume")}
    </RippleButton>
  );
};

export const SCOREResumeCard = ({
  test,
  onResume,
  className,
}: SCOREResumeCardProps) => {
  const { theme } = useTheme();
  const startedAtIso = test.student_start_time ?? null;
  const durationMinutes = test.duration_in_minutes ?? 0;
  const endIso =
    startedAtIso && durationMinutes > 0
      ? new Date(
          new Date(startedAtIso).getTime() + durationMinutes * 60_000,
        ).toISOString()
      : null;

  return (
    <VStack className={`${styles.card} ${className ?? ""}`}>
      <HStack className={styles.iconRow}>
        <Box className={styles.iconContainer}>
          <Icon name="book" size={32} color={theme.colors.primary[50]} />
        </Box>

        <VStack className={styles.courseInfo}>
          <Text
            variant="body"
            size="medium"
            weight="emphasized"
            color={theme.colors.text[10]}
            ellipsis
            className={styles.title}
          >
            {test.title}
          </Text>

          <Box>
            <StatusTag
              status={test.status}
              startDate={test.startDate}
              endDate={test.endDate}
            />
          </Box>

          <VStack className={styles.metaRow}>
            {test.startDate ? (
              <HStack className={styles.metaItem}>
                <Icon
                  name="calendar"
                  size={16}
                  color={theme.colors.neutral[40]}
                />
                <Text
                  variant="body"
                  size="medium"
                  color={theme.colors.neutral[40]}
                >
                  {formatDateTime(test.startDate)}
                </Text>
              </HStack>
            ) : null}

            {endIso ? <TimeRemaining endIso={endIso} /> : null}
          </VStack>
        </VStack>
      </HStack>

      <HStack className={styles.buttonRow}>
        <ResumeButton
          endIso={endIso}
          isExpiredFlag={test.isExpired === true}
          onPress={() => onResume(test)}
        />
      </HStack>
    </VStack>
  );
};
